import { Slot } from '@radix-ui/react-slot'
import { VariantProps, cva } from 'class-variance-authority'
import { cn } from 'lib/utils'
import * as React from 'react'

/**
 * T3 typographic primitive for non-heading text. Routes through the locked
 * product type scale (.t-*) defined in styles/design-system.css.
 *
 * Spec: playbook/design-system/foundations.md#product-type-scale.
 *
 *   variant="body"     → 14px paper, default body text
 *   variant="body-sm"  → 12.5px paper-dim, secondary / metadata
 *   variant="eyebrow"  → 10px mono uppercase, section labels
 *   variant="mono"     → 11px mono, IDs, timestamps, counters
 *
 * Tones override the variant default color (e.g. body in `paper-dim`):
 *   tone="paper" | "paper-dim" | "paper-faint" | "acid" | "destructive"
 *
 * Use this anywhere you'd otherwise reach for text-[Npx] or hardcoded
 * Tailwind classes — every product surface should be expressible as
 * <Heading> + <Text> + tokens.
 */
const textVariants = cva('', {
  variants: {
    variant: {
      body: 't-body',
      'body-sm': 't-body-sm',
      eyebrow: 't-eyebrow',
      mono: 't-mono-meta',
    },
    tone: {
      paper: 'text-paper',
      'paper-dim': 'text-paper-dim',
      'paper-faint': 'text-paper-faint',
      acid: 'text-acid',
      destructive: 'text-destructive',
    },
    weight: {
      regular: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
    },
  },
  defaultVariants: {
    variant: 'body',
  },
})

type Element = 'p' | 'span' | 'div' | 'label' | 'small'

export interface TextProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'color'>, VariantProps<typeof textVariants> {
  as?: Element
  asChild?: boolean
}

const Text = React.forwardRef<HTMLElement, TextProps>(
  ({ className, variant, tone, weight, as = 'span', asChild = false, ...props }, ref) => {
    const Comp = (asChild ? Slot : as) as React.ElementType
    return (
      <Comp
        ref={ref}
        className={cn(textVariants({ variant, tone, weight }), className)}
        data-slot="text"
        {...props}
      />
    )
  }
)

Text.displayName = 'Text'

export { Text, textVariants }
